import { useCallback, useEffect, useMemo, useState } from 'react';
import { attendanceAPI, employeesAPI } from '../lib/api';
import { toast } from 'sonner';

/**
 * Owns ALL filter state, async data fetching, and derived data for the
 * Attendance Management page.
 *
 * Performance model (June 2026 optimisation):
 *   • On open, only the lightweight daily-summary for the CURRENT PAY PERIOD is
 *     fetched (plus the employee list for the filter dropdown). No whole-history
 *     / whole-year loads.
 *   • Exceptions are DERIVED client-side from the daily-summary rows (each row
 *     already carries `exceptions` + `exception_status`), so no extra API call.
 *   • The Monthly Summary is fetched lazily — only when that tab is opened.
 */

const iso = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// Current Monthly pay period (24th -> 23rd), matching backend get_monthly_pay_period.
function currentMonthlyPayPeriod(today = new Date()) {
  const y = today.getFullYear();
  const m = today.getMonth();
  const d = today.getDate();
  let start;
  let end;
  if (d >= 24) {
    start = new Date(y, m, 24);
    end = new Date(y, m + 1, 23);
  } else {
    start = new Date(y, m - 1, 24);
    end = new Date(y, m, 23);
  }
  return { start: iso(start), end: iso(end) };
}

export function useAttendanceData() {
  const [loading, setLoading] = useState(true);
  const [monthlyLoading, setMonthlyLoading] = useState(false);
  const [dailySummary, setDailySummary] = useState([]);
  const [monthlySummary, setMonthlySummary] = useState([]);
  const [employees, setEmployees] = useState([]);

  // Which tab is active — drives lazy loading of the Monthly Summary.
  const [activeTab, setActiveTab] = useState('daily');

  // Filters. Default window = current pay period (populated into From/To so the
  // management view opens on the relevant period, not the whole month).
  const defaultPeriod = useMemo(() => currentMonthlyPayPeriod(), []);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [showAllEmployees, setShowAllEmployees] = useState(false);
  const [payCycleFilter, setPayCycleFilter] = useState('all');
  const [payPeriods, setPayPeriods] = useState([]);
  const [selectedPayPeriod, setSelectedPayPeriod] = useState('all');
  const [dateFrom, setDateFrom] = useState(defaultPeriod.start);
  const [dateTo, setDateTo] = useState(defaultPeriod.end);

  // Exception filter (client-side; no refetch).
  const [exceptionStatus, setExceptionStatus] = useState('active');

  // Resolve the shift-date window from the current filter state.
  const resolveWindow = useCallback(() => {
    let periodStart = null;
    let periodEnd = null;
    const usingCustomRange = Boolean(dateFrom || dateTo);
    if (usingCustomRange) {
      periodStart = dateFrom || dateTo;
      periodEnd = dateTo || dateFrom;
    } else if (selectedPayPeriod !== 'all') {
      const periodObj = payPeriods.find((p) => p.period_start === selectedPayPeriod);
      periodStart = periodObj?.period_start || null;
      periodEnd = periodObj?.period_end || null;
    }
    const monthParam = usingCustomRange ? null : selectedMonth;
    const yearParam = usingCustomRange ? null : selectedYear;
    return { periodStart, periodEnd, monthParam, yearParam };
  }, [dateFrom, dateTo, selectedPayPeriod, payPeriods, selectedMonth, selectedYear]);

  const fetchEmployees = useCallback(async () => {
    try {
      const response = await employeesAPI.getAll();
      setEmployees(response.data);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
    }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const hourlyOnly = !showAllEmployees;
    const cycleType = payCycleFilter === 'all' ? null : payCycleFilter;
    const { periodStart, periodEnd, monthParam, yearParam } = resolveWindow();

    try {
      const dailyRes = await attendanceAPI.getDailySummary(
        monthParam,
        yearParam,
        selectedEmployee !== 'all' ? selectedEmployee : null,
        hourlyOnly,
        cycleType,
        periodStart,
        periodEnd,
      );
      setDailySummary(dailyRes.data);
    } catch (error) {
      toast.error('Failed to load attendance data');
    } finally {
      setLoading(false);
    }
  }, [showAllEmployees, payCycleFilter, resolveWindow, selectedEmployee]);

  // Monthly Summary — lazy. Only fetched when the Monthly tab is active.
  const fetchMonthly = useCallback(async () => {
    setMonthlyLoading(true);
    const hourlyOnly = !showAllEmployees;
    const cycleType = payCycleFilter === 'all' ? null : payCycleFilter;
    const { periodStart, periodEnd, monthParam, yearParam } = resolveWindow();
    try {
      const monthlyRes = await attendanceAPI.getMonthlySummary(
        monthParam,
        yearParam,
        hourlyOnly,
        cycleType,
        periodStart,
        periodEnd,
      );
      setMonthlySummary(monthlyRes.data);
    } catch (error) {
      console.error('Failed to load monthly summary', error);
    } finally {
      setMonthlyLoading(false);
    }
  }, [showAllEmployees, payCycleFilter, resolveWindow]);

  // Exceptions derived client-side from the daily rows (no extra request).
  const exceptions = useMemo(() => {
    const rows = dailySummary.filter(
      (d) => (d.exceptions && d.exceptions.length) || d.exception_status,
    );
    if (exceptionStatus === 'all') return rows;
    return rows.filter((r) => (r.exception_status || 'active') === exceptionStatus);
  }, [dailySummary, exceptionStatus]);

  // Initial + reactive load when filters change (daily only).
  useEffect(() => {
    fetchData();
    fetchEmployees();
  }, [fetchData, fetchEmployees]);

  // Lazy-load the Monthly Summary when its tab is opened or filters change while active.
  useEffect(() => {
    if (activeTab === 'monthly') fetchMonthly();
  }, [activeTab, fetchMonthly]);

  // Fetch available pay periods when cycle filter or year changes.
  useEffect(() => {
    const fetchPayPeriods = async () => {
      if (payCycleFilter === 'all') {
        setPayPeriods([]);
        setSelectedPayPeriod('all');
        return;
      }
      try {
        const response = await attendanceAPI.getPayPeriods(payCycleFilter, selectedYear);
        setPayPeriods(response.data || []);
        setSelectedPayPeriod('all');
      } catch (error) {
        console.error('Failed to fetch pay periods', error);
        setPayPeriods([]);
      }
    };
    fetchPayPeriods();
  }, [payCycleFilter, selectedYear]);

  const refresh = useCallback(() => {
    fetchData();
    if (activeTab === 'monthly') fetchMonthly();
  }, [fetchData, fetchMonthly, activeTab]);

  return {
    // Data
    loading, monthlyLoading, dailySummary, monthlySummary, exceptions, employees,
    // Tab control (drives lazy loading)
    activeTab, setActiveTab,
    // Filter state + setters
    selectedMonth, setSelectedMonth,
    selectedYear, setSelectedYear,
    selectedEmployee, setSelectedEmployee,
    showAllEmployees, setShowAllEmployees,
    payCycleFilter, setPayCycleFilter,
    payPeriods,
    selectedPayPeriod, setSelectedPayPeriod,
    dateFrom, setDateFrom,
    dateTo, setDateTo,
    exceptionStatus, setExceptionStatus,
    // Actions
    fetchData: refresh,
    refresh,
  };
}
